import { useCallback, useMemo, useState } from "react";

import uniq from "lodash/uniq";
import { useSelector } from "react-redux";

import { i18nFilter } from "app/filters/i18nFilter";
import type { ICustomSegmentsGroup } from "app/services/segments/segmentsApiService";
import SegmentsApiService from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { loadCustomSegmentsMetadata } from "../../config/segmentsConfigHelpers";
import { MAX_SEGMENTS_IN_GROUP } from "../SegmentsAnalysisHelper";
import { MODE } from "../SegmentsAnalysisTrafficContainerMode";

export enum ESegmentGroupMode {
    GROUP_LIST = 0,
    NEW_GROUP = 1,
    ALL_ITEMS_EXISTS = 2,
    MAX_ITEMS = 3,
}

export interface ISegmentGroupItem {
    id: string;
    text: string;
}

interface ISegmentGroupSelectionControllerParams {
    selectedSegmentIds: string[];
    clearAllSelectedRows: () => void;
    showToast: (href: string | null, text: string, label: string | null) => void;
}

export const useSegmentGroupSelectionController = ({
    selectedSegmentIds,
    clearAllSelectedRows,
    showToast,
}: ISegmentGroupSelectionControllerParams) => {
    const i18n = i18nFilter();
    const segmentGroups = useSelector(
        (state: { segmentsModule?: { customSegmentsMeta?: { SegmentGroups?: ICustomSegmentsGroup[] } } }) =>
            state.segmentsModule?.customSegmentsMeta?.SegmentGroups,
    );
    const segmentsService = useMemo(() => new SegmentsApiService(), []);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [mode, setMode] = useState<ESegmentGroupMode>(ESegmentGroupMode.GROUP_LIST);
    const [currentGroupName, setCurrentGroupName] = useState<string | null>(null);

    const groups = useMemo<ISegmentGroupItem[]>(
        () =>
            segmentGroups?.map((group: ICustomSegmentsGroup) => ({
                text: group.name,
                id: group.id,
            })) ?? [],
        [segmentGroups],
    );

    const showGroupLinkToast = useCallback(
        (text: string, label: string, groupId: string) => {
            const currentModule = proNavigator.getCurrentModule();
            const segmentAnalysisStateName = `${currentModule}-analysis-traffic`;
            const linkToGroup = proNavigator.href(segmentAnalysisStateName, {
                mode: MODE.group,
                country: 840,
                duration: "3m",
                id: groupId,
            });
            showToast(linkToGroup, text, label);
        },
        [showToast],
    );

    const resetAfterSuccess = useCallback(
        (isNewGroup: boolean, groupId?: string, segmentIdsLength?: number) => {
            clearAllSelectedRows();
            setMode(ESegmentGroupMode.GROUP_LIST);
            setErrorMessage(null);
            setError(false);

            const text = isNewGroup
                ? i18n("table.selection.segments.groupcreated", { segmentsAdded: segmentIdsLength })
                : i18n("table.selection.segments.groupupdated", { segmentsAdded: segmentIdsLength });
            showGroupLinkToast(text, i18n("table.selection.segments.seegroup"), groupId);
        },
        [clearAllSelectedRows, i18n, showGroupLinkToast],
    );

    const handleCancel = useCallback(() => {
        setMode(ESegmentGroupMode.GROUP_LIST);
        setError(false);
        setErrorMessage(null);
    }, []);

    const handleCreateGroup = useCallback(
        async (name: string) => {
            allTrackers.trackEvent(
                "add to segment group",
                "submit-ok",
                `create new group/Save/${name};${selectedSegmentIds.length}`,
            );
            setIsLoading(true);
            try {
                const newGroup = await segmentsService.createCustomSegmentsGroup({
                    name,
                    segments: selectedSegmentIds,
                });
                resetAfterSuccess(true, newGroup?.id, selectedSegmentIds.length);
                await loadCustomSegmentsMetadata(true);
            } catch {
                // silenced — matches legacy behavior
            } finally {
                setIsLoading(false);
            }
        },
        [selectedSegmentIds, segmentsService, resetAfterSuccess],
    );

    const handleExistingGroupClick = useCallback(
        async ({ id, text }: ISegmentGroupItem) => {
            const group = SegmentsUtils.getSegmentGroupById(segmentGroups, id);
            const currentSegments: string[] = group.segments;
            const withoutDuplications = uniq([...currentSegments, ...selectedSegmentIds]);
            const newSegmentsCount = withoutDuplications.length - currentSegments.length;

            if (newSegmentsCount === 0) {
                setMode(ESegmentGroupMode.ALL_ITEMS_EXISTS);
                setCurrentGroupName(text);
                return;
            }

            if (withoutDuplications.length > MAX_SEGMENTS_IN_GROUP) {
                setMode(ESegmentGroupMode.MAX_ITEMS);
                return;
            }

            allTrackers.trackEvent(
                "add to Custom segments group",
                "click",
                `edit segment group/Save/${text};${newSegmentsCount}`,
            );
            try {
                const update = await segmentsService.updateCustomSegmentsGroup({
                    name: text,
                    id: group.id,
                    segments: withoutDuplications,
                });
                await loadCustomSegmentsMetadata(true);
                if (update) {
                    resetAfterSuccess(false, group.id);
                }
            } catch {
                // silenced — matches legacy behavior
            }
        },
        [segmentGroups, selectedSegmentIds, segmentsService, resetAfterSuccess],
    );

    const handleNewGroupClick = useCallback(() => {
        if (selectedSegmentIds.length > MAX_SEGMENTS_IN_GROUP) {
            setMode(ESegmentGroupMode.MAX_ITEMS);
        } else {
            setMode(ESegmentGroupMode.NEW_GROUP);
        }
    }, [selectedSegmentIds.length]);

    const handleGroupClick = useCallback(
        async (item: { id?: string; text?: string }) => {
            if (!item.id) {
                handleNewGroupClick();
                allTrackers.trackEvent("add to segment group", "click", "create new group");
            } else {
                await handleExistingGroupClick(item as ISegmentGroupItem);
            }
        },
        [handleExistingGroupClick, handleNewGroupClick],
    );

    return {
        groups,
        mode,
        isLoading,
        error,
        errorMessage,
        currentGroupName,
        handleCancel,
        handleCreateGroup,
        handleGroupClick,
    };
};
